import React from 'react';
import { Music, Mail, Clock, Megaphone } from 'lucide-react';

/**
 * "What's new": song choices go out once per project. ADMIN/STAFF (operator ruling, 2026-09-15).
 *
 * Single screen: what changed under them, what reaches them, and the questions they will be
 * asked. The editor variant says what editors now do; the client variant what clients must do.
 */

const Eyebrow = ({ children }) => (
  <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-500 block">
    {children}
  </span>
);

export function SongOnceAdminAnnouncement({ onAcknowledge }) {
  return (
    <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-ug-raised border border-ug-border shadow-2xl"
         data-testid="whats-new-song-once-admin">
      <div className="p-6 pb-5 border-b border-ug-border-subtle">
        <div className="flex items-center gap-2 mb-3">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-ug-gold/15 text-ug-gold border border-ug-gold/25">New</span>
          <Music className="w-3.5 h-3.5 text-ug-gold" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl text-zinc-100 mb-2">Song choices go out once per project</h2>
        <p className="text-[13px] text-zinc-100/75 leading-relaxed">
          Editors send one set of song choices. The client keeps any of them and must send links to their own
          song(s) for anything they don't like, within 24 hours. There is no second set and no override.
        </p>
      </div>
      <div className="p-6 py-5 bg-ug-surface/30 space-y-3">
        <Eyebrow>What reaches you</Eyebrow>
        {/* flex + gap, not space-y: the rows must never touch, with or without a wrapper element. */}
        <div className="flex flex-col gap-2">
          {[[Mail, 'An email with the client\'s final songs on every answer, the same list the editor gets'],
            [Clock, 'The 24-hour rule is unchanged: no reply in time moves the project back in the queue'],
            [Megaphone, 'Clients who have us choose their music see a one-time notice explaining the change']].map(([Icon, text], i) => (
            <div key={i} className="flex items-center gap-2 rounded bg-ug-surface border border-ug-border-strong px-2.5 py-2">
              <Icon className="w-3.5 h-3.5 text-oxidized-teal shrink-0" />
              <span className="text-[11px] text-zinc-100/75">{text}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 py-5 space-y-2.5 border-t border-ug-border-subtle">
        {[
          ['Further song questions go through chat', 'Both sides are told that. A second set cannot be sent from the portal, by anyone.'],
          ['The client\'s songs are final', 'The editor uses them as sent. Licensing a song the client supplies is the client\'s responsibility.'],
          ['Song changes after delivery are $50 per song', 'Changes before a version is delivered stay free. Waivers are Ryan\'s call.'],
        ].map(([t, d], i) => (
          <div key={i} className="flex gap-3">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ug-gold/15 border border-ug-gold/25 text-ug-gold font-sans text-[10px] font-semibold flex items-center justify-center">{i + 1}</span>
            <p className="text-[12px] text-zinc-100/75 leading-relaxed">
              <span className="text-zinc-100 font-medium">{t}.</span> {d}
            </p>
          </div>
        ))}
      </div>
      <div className="p-6 pt-5 border-t border-ug-border-subtle bg-ug-surface/20">
        <button data-testid="whats-new-next" onClick={() => onAcknowledge && onAcknowledge()}
                className="tap-auto w-full py-2.5 rounded-lg bg-gold-leaf text-obsidian font-sans text-[11px] font-semibold uppercase tracking-wider">
          Got it
        </button>
      </div>
    </div>
  );
}

export default SongOnceAdminAnnouncement;
